import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, money, imgSrc } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { ProductCard } from "@/components/ProductCard";
import { SizeGuide, SizeGuideButton } from "@/components/SizeGuide";
import { MaskLines, Reveal } from "@/components/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const SizePicker = ({ product, size, setSize }) => (
  <div className="mt-8">
    <p className="eyebrow">Select size</p>
    <div className="mt-3 grid grid-cols-5 gap-2" data-testid="size-selector">
      {(product.sizes || []).map((s) => {
        const out = (product.stock?.[s] ?? 0) <= 0;
        return (
          <button
            key={s}
            disabled={out}
            data-testid={`size-option-${s}`}
            onClick={() => setSize(s)}
            className={`border py-3 font-mono text-sm transition-colors duration-200 ${size === s ? "border-ink bg-ink text-bone" : "border-line hover:border-ink"} ${out ? "cursor-not-allowed text-stone line-through opacity-50" : ""}`}
          >
            {s}
          </button>
        );
      })}
    </div>
  </div>
);

export default function ProductDetail() {
  const { id } = useParams();
  const { add } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [size, setSize] = useState("");
  const [guideOpen, setGuideOpen] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    setProduct(null);
    setSize("");
    api.get(`/products/${id}`).then((r) => {
      setProduct(r.data);
      return api.get("/products", { params: { category: r.data?.category } });
    }).then((r) => setRelated((Array.isArray(r?.data) ? r.data : []).filter((p) => p.product_id !== id).slice(0, 4)))
      .catch(() => setError(true));
  }, [id]);

  if (error) return <main className="px-5 pt-40 text-center font-display text-4xl" data-testid="product-not-found">Garment not found. <Link to="/shop" className="italic underline">Back to shop</Link></main>;
  if (!product) return <main className="min-h-screen pt-40" />;

  const onAdd = () => {
    if (!size) return toast.error("Please choose a size first");
    add(product, size, 1);
  };
  const left = size ? product.stock?.[size] ?? 0 : null;

  return (
    <main data-testid="product-detail-page" className="mx-auto max-w-[1600px] px-5 pb-28 pt-24 md:px-10 md:pt-32">
      <div className="grid gap-10 md:grid-cols-12">
        <div className="space-y-4 md:col-span-7">
          {(product.images || []).map((src, i) => (
            <motion.div key={src} className="relative aspect-[4/5] overflow-hidden bg-sand" initial={{ clipPath: "inset(0 0 100% 0)" }} animate={{ clipPath: "inset(0 0 0% 0)" }} transition={{ duration: 1.3, delay: 0.1 + i * 0.15, ease: [0.16, 1, 0.3, 1] }}>
              <img src={imgSrc(src)} alt={`${product.name} view ${i + 1}`} className="h-full w-full object-cover" data-testid={`product-image-${i}`} />
            </motion.div>
          ))}
        </div>
        <aside className="md:col-span-5">
          <div className="md:sticky md:top-28">
            <Link to={`/shop?category=${product.category}`} className="eyebrow link-underline" data-testid="product-category-link">{product.category}</Link>
            <h1 data-testid="product-name" className="mt-4 font-display text-5xl font-light leading-none md:text-7xl">
              <MaskLines lines={[product.name]} delay={0.15} />
            </h1>
            <p data-testid="product-price" className="mt-6 font-mono text-lg">{money(product.price)}</p>
            <p className="mt-2 text-sm text-stone">Colour — {product.color}</p>
            <SizePicker product={product} size={size} setSize={setSize} />
            {left !== null && left > 0 && left <= 5 && <p className="mt-3 font-mono text-xs text-signal" data-testid="low-stock">Only {left} left in {size}</p>}
            {(product.sizes || []).includes("S") && <div className="mt-4"><SizeGuideButton onClick={() => setGuideOpen(true)} /></div>}
            <SizeGuide open={guideOpen} onOpenChange={setGuideOpen} />
            <button data-testid="add-to-cart-button" onClick={onAdd} className="mt-8 w-full rounded-full bg-ink py-5 text-sm tracking-wide text-bone transition-colors duration-300 hover:bg-signal">
              {size ? `Add to bag — ${size}` : "Add to bag"}
            </button>
            <Accordion type="single" collapsible defaultValue="d" className="mt-10 border-t border-line">
              <AccordionItem value="d" className="border-line">
                <AccordionTrigger data-testid="accordion-description" className="font-mono text-xs uppercase tracking-[0.2em]">Description</AccordionTrigger>
                <AccordionContent className="text-base leading-relaxed text-stone">{product.description}</AccordionContent>
              </AccordionItem>
              <AccordionItem value="s" className="border-line">
                <AccordionTrigger data-testid="accordion-shipping" className="font-mono text-xs uppercase tracking-[0.2em]">Shipping & payment</AccordionTrigger>
                <AccordionContent className="text-base leading-relaxed text-stone">Shipping and taxes are calculated at checkout. Payments are processed securely by Stripe.</AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </aside>
      </div>
      {related.length > 0 && (
        <section className="mt-32" data-testid="related-products">
          <Reveal><h2 className="font-display text-4xl font-light md:text-6xl">Wear it <em>with.</em></h2></Reveal>
          <div className="mt-10 grid grid-cols-2 gap-x-4 gap-y-10 md:grid-cols-4 md:gap-x-6">
            {related.map((p, i) => <Reveal key={p.product_id} delay={i * 0.06}><ProductCard product={p} index={i} /></Reveal>)}
          </div>
        </section>
      )}
    </main>
  );
}
